/**
 * Reimbursment Form.
 *
 * @format
 */

import React, { useMemo, useState } from 'react';
import ModalFormContainer from '../../form-components/form-containers/modal-form-container';
import SimpleFormContainer from '../../form-components/form-containers/simple-form-container';
import { getFormFields } from '@forms/form-types/varian-form-paragraph/get-form-fields';

type VarianInlineFormProps = {
  linkText: string;
  linkType?: string;
  linkClasses?: string;
  formTitle: string;
  formFields: string;
  formDescription?: string;
  formType: string;
  formName: string;
  formId?: string;
  formContainer?: string;
  emailTo: string;
  responseText?: string;
  productId?: string;
  responseMessage?: string;
  emailRedirect?: string;
  emailTitle?: string;
};

/**
 * Varian Inline Form.
 */
const VarianInlineForm: React.FC<{ formData: VarianInlineFormProps }> = ({
  formData,
}) => {
  const {
    linkText,
    linkType,
    linkClasses,
    formTitle,
    formFields,
    formDescription,
    formContainer,
    formType,
    formName,
    formId,
    emailTo,
    responseText,
    productId,
    responseMessage,
    emailRedirect,
    emailTitle,
  }: VarianInlineFormProps = formData;

  const title = formTitle ?? 'Contact Us';
  const [fields, setFields] = useState(null);
  const { path, key } = getFormFields(formFields);
  const container = formContainer ?? 'modal';
  const response =
    responseText ??
    'Thank you for reaching out; someone will get back to you shortly.';

  /**
   * Get and Set Field Information (email form only).
   */
  useMemo(() => {
    async function fetchFormFields() {
      if (path && key) {
        await import(`../${path}`).then((response) => {
          if (response[key] && response[key].length > 0) {
            setFields(response[key]);
          }
        });
      }
    }

    fetchFormFields();
  }, [key, path]);

  if (fields) {
    /**
     * Simple on page form.
     */
    if (container === 'simple') {
      return formType === 'email' ? (
        <SimpleFormContainer
          formId={'-1'}
          formFields={fields}
          formType={formName || `varian-inline-email-form`}
          formTitle={title}
          formDescription={formDescription}
          emailTo={emailTo}
          emailSubject={`${title} submission.`}
          replyToField={`Email`}
          requestType={`email`}
          requestUrl={`/varian_mailman/send`}
          submitResponse={response}
          responseMessage={responseMessage}
          emailRedirect={emailRedirect === 'true'}
          emailTitle={emailTitle}
        />
      ) : (
        <SimpleFormContainer
          formId={formId}
          formFields={fields}
          formDescription={formDescription}
          formType={formName || `varian-inline-marketo-form`}
          formTitle={title}
          submitResponse={response}
          productFormType={productId}
        />
      );
    }

    /**
     * Modal form.
     */
    return formType === 'email' ? (
      <ModalFormContainer
        formId={'-1'}
        formFields={fields}
        formType={formName || `varian-inline-email-form`}
        formTitle={title}
        formDescription={formDescription}
        emailTo={emailTo}
        emailSubject={`${title} submission.`}
        replyToField={`Email`}
        requestType={`email`}
        requestUrl={`/varian_mailman/send`}
        linkType={linkType || `link`}
        linkText={linkText}
        linkClasses={linkClasses}
        submitResponse={response}
        responseMessage={responseMessage}
        emailRedirect={emailRedirect === 'true'}
        emailTitle={emailTitle}
      />
    ) : (
      <ModalFormContainer
        formId={formId}
        formFields={fields}
        formDescription={formDescription}
        formType={formName || `varian-inline-marketo-form`}
        formTitle={title}
        linkClasses={linkClasses}
        linkType={`button`}
        linkText={linkText}
        submitResponse={response}
        productFormType={productId}
      />
    );
  }
  return null;
};
export default VarianInlineForm;
